import React, {
  MouseEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'
import { MenuItem } from '../../types'
import styles from './SubNavMenu.module.scss'
import { useI18n } from '../../i18n/I18nProvider'

interface SideBarLayoutProps {
  children: ReactNode
  menuItems: MenuItem[]
  title: string
  basePath?: string
  onMenuItemClick?: () => void
}

const VIEWPORT_MARGIN = 12
const MENU_MAX_HEIGHT = 560

const SubNavMenu = ({
  children,
  menuItems,
  title,
  basePath,
  onMenuItemClick,
}: SideBarLayoutProps) => {
  const { t } = useI18n()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [supportsHover, setSupportsHover] = useState<boolean>(true)
  const [opensUpward, setOpensUpward] = useState<boolean>(false)
  const [menuMaxHeight, setMenuMaxHeight] = useState<number>(MENU_MAX_HEIGHT)
  const [menuCoords, setMenuCoords] = useState<{
    left: number
    top?: number
    bottom?: number
  }>({ left: 0 })
  const rootRef = useRef<HTMLDivElement | null>(null)
  const sideBarRef = useRef<HTMLDivElement | null>(null)
  const closeTimerRef = useRef<number | undefined>(undefined)

  const normalizePath = (path: string) => {
    if (!basePath || path.startsWith('/') || path.includes('?')) {
      return path
    }

    const normalizedBase = basePath.endsWith('/')
      ? basePath.slice(0, -1)
      : basePath

    return `${normalizedBase}/${path}`
  }

  const isMenuItemActive = (itemPath: string) => {
    const normalized = normalizePath(itemPath)
    const [pathname, search = ''] = normalized.split('?')

    if (location.pathname !== pathname) {
      return false
    }

    if (!search) {
      return true
    }

    const itemParams = new URLSearchParams(search)
    const currentParams = new URLSearchParams(location.search)

    return Array.from(itemParams.entries()).every(([key, value]) => {
      if (key === 'tab') {
        return (currentParams.get(key) || 'daily') === value
      }

      return currentParams.get(key) === value
    })
  }

  const updateMenuPosition = () => {
    if (!rootRef.current) {
      return
    }

    const triggerRect = rootRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - triggerRect.top - VIEWPORT_MARGIN
    const spaceAbove = triggerRect.bottom - VIEWPORT_MARGIN

    const measuredHeight = sideBarRef.current?.scrollHeight
    const estimatedHeight =
      measuredHeight && measuredHeight > 0
        ? measuredHeight
        : Math.min(
            MENU_MAX_HEIGHT,
            52 + Math.max(menuItems.length, 1) * 48,
          )

    const shouldOpenUpward =
      spaceBelow < estimatedHeight && spaceAbove > spaceBelow

    const availableSpace = shouldOpenUpward ? spaceAbove : spaceBelow

    setOpensUpward(shouldOpenUpward)
    setMenuMaxHeight(
      Math.max(160, Math.min(MENU_MAX_HEIGHT, availableSpace)),
    )
    setMenuCoords(
      shouldOpenUpward
        ? {
            left: Math.round(triggerRect.right + 10),
            bottom: Math.round(window.innerHeight - triggerRect.bottom - 6),
          }
        : {
            left: Math.round(triggerRect.right + 10),
            top: Math.round(triggerRect.top - 6),
          },
    )
  }

  const openMenu = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
    }
    closeTimerRef.current = undefined
    updateMenuPosition()
    setIsOpen(true)
  }

  const scheduleClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
    }
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false)
    }, 250)
  }

  const closeMenu = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = undefined
    }

    setIsOpen(false)
  }

  const toggleMenu = () => {
    if (isOpen) {
      closeMenu()
      return
    }

    openMenu()
  }

  const handleTriggerClick = (event: MouseEvent<HTMLElement>) => {
    if (supportsHover) {
      return
    }

    if (!isOpen) {
      event.preventDefault()
      event.stopPropagation()
      openMenu()
    }
  }

  let trigger = children

  if (isValidElement(children)) {
    const child = children as ReactElement<{
      onClick?: (event: MouseEvent<HTMLElement>) => void
      'aria-expanded'?: boolean
      'aria-haspopup'?: 'menu'
    }>

    trigger = cloneElement(child, {
      onClick: (event: MouseEvent<HTMLElement>) => {
        handleTriggerClick(event)

        if (!event.defaultPrevented) {
          child.props.onClick?.(event)
        }
      },
      'aria-expanded': isOpen,
      'aria-haspopup': 'menu',
    })
  }

  useEffect(() => {
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)')

    const updateHoverSupport = (event?: MediaQueryListEvent) => {
      setSupportsHover(event ? event.matches : hoverQuery.matches)
    }

    updateHoverSupport()
    hoverQuery.addEventListener('change', updateHoverSupport)

    return () => {
      hoverQuery.removeEventListener('change', updateHoverSupport)

      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (supportsHover || !isOpen) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        closeMenu()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isOpen, supportsHover])

  useLayoutEffect(() => {
    if (!isOpen) {
      return
    }

    updateMenuPosition()

    const handleReposition = () => {
      updateMenuPosition()
    }

    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [isOpen, menuItems.length])

  return (
    <div
      ref={rootRef}
      className={styles.root}
      onMouseEnter={supportsHover ? openMenu : undefined}
      onMouseLeave={supportsHover ? scheduleClose : undefined}
    >
      <div
        className={styles.children}
        onClick={!supportsHover ? toggleMenu : undefined}
      >
        {trigger}
      </div>
      {createPortal(
        <div
          ref={sideBarRef}
          className={`${styles.sideBar} ${isOpen ? styles.sideBarOpen : ''} ${
            opensUpward ? styles.sideBarUp : ''
          }`}
          style={{
            maxHeight: menuMaxHeight,
            left: menuCoords.left,
            ...(opensUpward
              ? { top: 'auto', bottom: menuCoords.bottom }
              : { top: menuCoords.top, bottom: 'auto' }),
          }}
          role="menu"
          aria-hidden={!isOpen}
          onMouseEnter={supportsHover ? openMenu : undefined}
          onMouseLeave={supportsHover ? scheduleClose : undefined}
        >
          <div className={styles.header}>
            <span className={styles.headerTitle}>{t(title)}</span>
          </div>
          {menuItems?.length > 0 &&
            menuItems.map((item) => (
              <NavLink
                key={item.path}
                role="menuitem"
                title={t(item.name)}
                className={() =>
                  isMenuItemActive(item.path)
                    ? styles.activeNavLink
                    : styles.navLink
                }
                to={normalizePath(item.path)}
                onClick={() => {
                  closeMenu()
                  onMenuItemClick?.()
                }}
              >
                <div className={styles.icon}>
                  <item.icon />
                </div>
                <div className={styles.name}>{t(item.name)}</div>
              </NavLink>
            ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

export default SubNavMenu
